import React, { useContext, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { EventBasedTriggerMetadata } from '@reach/shared-types/business-automation';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { StepDialogFooter } from '../StepDialogFooter';
import { useSegmentDefinitions } from '../useSegmentDefinitions';
import { SegmentListStates } from './SegmentListStates';
import styles from './TriggerStep.module.css';

interface SegmentTriggerConfigDialogProps {
  onClose: () => void;
  triggerMetadata: EventBasedTriggerMetadata;
}

export const SegmentTriggerConfigDialog: React.FC<
  SegmentTriggerConfigDialogProps
> = ({ onClose, triggerMetadata }) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateTrigger } = editor;
  const {
    data: segmentDefinitions,
    isLoading,
    error,
  } = useSegmentDefinitions();

  const currentSegmentId = triggerMetadata.eventFilter?.segment_id ?? null;
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    currentSegmentId,
  );

  useEffect(() => {
    setSelectedSegmentId(triggerMetadata.eventFilter?.segment_id ?? null);
  }, [triggerMetadata]);

  const handleOK = () => {
    const newTriggerMetadata = structuredClone(triggerMetadata);
    newTriggerMetadata.eventFilter.segment_id = selectedSegmentId || '';
    updateTrigger(newTriggerMetadata);
    onClose();
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.segmentTriggerDialog} open>
        <header>
          <hgroup>
            <h2>Select Audience Segment</h2>
            <p>Choose which segment will trigger this campaign</p>
          </hgroup>
          <button onClick={onClose} type="button" aria-label="Close">
            <X />
          </button>
        </header>

        <main>
          <SegmentListStates
            isLoading={isLoading}
            error={error}
            isEmpty={!segmentDefinitions?.length}
          />
          {segmentDefinitions && segmentDefinitions.length > 0 && (
            <ul>
              {segmentDefinitions.map(segment => {
                const { id, name, description, canonicalVersionId } = segment;
                const isSelected = selectedSegmentId === canonicalVersionId;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      data-selected={isSelected || undefined}
                      onClick={() => setSelectedSegmentId(canonicalVersionId)}>
                      <strong>{name}</strong>
                      {description && <p>{description}</p>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </main>

        <StepDialogFooter onCancel={onClose} onOK={handleOK} />
      </dialog>
    </>
  );
};
